import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { toast } from "react-toastify";
import AssistantChartCard from "@/components/assistant/charts/AssistantChartCard";
import chartRegistry from "@/components/assistant/charts/ChartRegistry";
import AssistantDownloadCard from "@/components/assistant/AssistantDownloadCard";
import apiClient from "@/services/apiClient";
import { BACKEND_URL } from "@/config";
import i18n from "@/i18n";
import * as C from "@/const";

const toSafeMessage = (message, fallback) =>
  typeof message === "string" && message.trim() ? message : fallback;

// Show a user-friendly error toast for failed non-GET API calls
export const showApiError = (message) => {
  const fallback = i18n?.t
    ? i18n.t("common.genericError", "Something went wrong. Please try again.")
    : "Something went wrong. Please try again.";
  toast.error(toSafeMessage(message, fallback));
};

export const showSuccess = (message) => {
  const fallback = i18n?.t
    ? i18n.t("common.genericSuccess", "Operation completed successfully.")
    : "Operation completed successfully.";
  toast.success(toSafeMessage(message, fallback));
};

export const showWarning = (message) => {
  const fallback = i18n?.t
    ? i18n.t("common.genericWarning", "Please review before proceeding.")
    : "Please review before proceeding.";
  toast.warning(toSafeMessage(message, fallback));
};

export const showInfo = (message) => {
  const fallback = i18n?.t
    ? i18n.t("common.genericInfo", "No additional details available.")
    : "No additional details available.";
  toast.info(toSafeMessage(message, fallback));
};

// Set item in localStorage with expiry time
export function setLocalStorageWithExpiry(key, value, ttlMinutes) {
  const now = new Date();
  const expiryTime = now.getTime() + ttlMinutes * 60 * 1000;

  const item = {
    value: value,
    expiry: expiryTime,
  };

  localStorage.setItem(key, JSON.stringify(item));
}

// util function to get item from localStorage with expiry check
export function getLocalStorageWithExpiry(key) {
  const itemStr = localStorage.getItem(key);

  if (!itemStr) {
    return null;
  }

  try {
    const item = JSON.parse(itemStr);
    const now = new Date();

    // Check if item has expired
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch (e) {
    localStorage.removeItem(key);
    return null;
  }
}

export const downloadAssistantFile = async (downloadUrl, fallbackFilename) => {
  const fullUrl = downloadUrl.startsWith("http")
    ? downloadUrl
    : `${BACKEND_URL}${downloadUrl.startsWith("/") ? "" : "/"}${downloadUrl}`;

  try {
    const response = await apiClient.get(fullUrl, {
      responseType: "blob",
    });

    const blob = response.data;
    const disposition = response.headers
      ? typeof response.headers.get === "function"
        ? response.headers.get("content-disposition")
        : response.headers["content-disposition"]
      : null;

    let filename = fallbackFilename || "download";
    if (disposition) {
      // 1. Try RFC 5987 filename* (e.g., filename*=UTF-8''%e3%83%ac%e3%83%9d%e3%83%bc%e3%83%88.xlsx)
      const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      if (utf8Match && utf8Match[1]) {
        try {
          filename = decodeURIComponent(utf8Match[1].trim());
        } catch {
          // Fall back if decoding fails
        }
      }
      if (filename === (fallbackFilename || "download")) {
        // 2. Fall back to standard filename="..." or filename=...
        const match =
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, "").trim();
        }
      }
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    if (error.response?.status === 401) {
      clearAuthStorage();
      window.location.href = "/login";
      return false;
    }
    console.error("Assistant file download error:", error);
    showApiError(
      error.response?.data?.detail || error.message || "Failed to download file.",
    );
    throw error;
  }
};

export const isChartCodeElement = (className, childrenText) => {
  if (!className || typeof className !== "string") return false;

  // Intercept any block with dedicated chart/vega languages
  if (/language-(geo-chart|vega-lite|vega_lite|vega)/i.test(className)) {
    return true;
  }

  // Smart-sniff json blocks to check if they contain a valid chart specification
  if (className.includes("language-json")) {
    try {
      const rawString = String(childrenText).replace(/\n$/, "").trim();
      const parsed = JSON.parse(rawString);
      if (parsed && typeof parsed === "object") {
        // 1. Decoupled Schema Verification (Checks canonical Vega/Vega-Lite independently of type field)
        if (
          (typeof parsed.$schema === "string" && parsed.$schema.includes("vega")) ||
          (typeof parsed.spec?.$schema === "string" && parsed.spec.$schema.includes("vega"))
        ) {
          return true;
        }

        // 2. Custom/Proprietary Chart Type Verification
        const type = parsed.type;
        if (type && typeof type === "string") {
          const lowerType = type.toLowerCase().trim();
          if (chartRegistry.has(lowerType)) {
            return true;
          }
        }
      }
    } catch (e) {
      // Ignored: not valid JSON, so not a chart
    }
  }

  return false;
};

const MarkdownParagraph = ({ children, ...pProps }) => {
  const hasBlockChild = React.Children.toArray(children).some(
    (child) =>
      React.isValidElement(child) &&
      ((typeof child.props?.href === "string" &&
        child.props.href.includes("/ai-assistant/download/")) ||
        child.type === AssistantDownloadCard)
  );
  if (hasBlockChild) {
    return <div {...pProps}>{children}</div>;
  }
  return <p {...pProps}>{children}</p>;
};

const MarkdownAnchor = ({ href, children, ...aProps }) => {
  const isAssistantDownload =
    href && href.includes("/ai-assistant/download/");
  if (isAssistantDownload) {
    return (
      <AssistantDownloadCard href={href}>
        {children}
      </AssistantDownloadCard>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...aProps}
    >
      {children}
    </a>
  );
};

const MarkdownPre = ({ children, ...preProps }) => {
  const hasChart = React.Children.toArray(children).some(
    (child) =>
      React.isValidElement(child) &&
      isChartCodeElement(child.props?.className, child.props?.children)
  );
  if (hasChart) {
    return <>{children}</>;
  }
  return <pre {...preProps}>{children}</pre>;
};

const MarkdownCode = ({ inline, className, children, ...codeProps }) => {
  if (!inline && isChartCodeElement(className, children)) {
    const rawString = String(children).replace(/\n$/, "").trim();
    try {
      const chartSpec = JSON.parse(rawString);
      return <AssistantChartCard spec={chartSpec} />;
    } catch (e) {
      if (/language-(geo-chart|vega-lite|vega_lite|vega)/i.test(className)) {
        return (
          <div
            className="assistant-chart-streaming-card p-3 my-2 bg-light rounded border border-light-subtle d-flex align-items-center gap-2 text-muted small"
            data-testid="chart-streaming-placeholder"
          >
            <span
              className="spinner-border spinner-border-sm text-primary"
              role="status"
              aria-hidden="true"
            />
            <span>Generating visualization...</span>
          </div>
        );
      }
      return (
        <pre className="p-3 bg-light rounded text-muted small">
          <code>{rawString}</code>
        </pre>
      );
    }
  }
  return (
    <code className={className} {...codeProps}>
      {children}
    </code>
  );
};

const DEFAULT_MARKDOWN_COMPONENTS = {
  p: MarkdownParagraph,
  a: MarkdownAnchor,
  pre: MarkdownPre,
  code: MarkdownCode,
};

const DEFAULT_REHYPE_PLUGINS = [rehypeRaw];

export const ReactMarkdownWithGFMPlugin = React.memo((props) => {
  const { children, components, rehypePlugins, ...restProps } = props;
  const mergedComponents = React.useMemo(() => {
    if (!components) return DEFAULT_MARKDOWN_COMPONENTS;
    return {
      ...DEFAULT_MARKDOWN_COMPONENTS,
      ...components,
    };
  }, [components]);

  const resolvedRehypePlugins =
    rehypePlugins !== undefined ? rehypePlugins : DEFAULT_REHYPE_PLUGINS;

  return (
    <div className="markdown" style={{ overflowX: "auto" }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={resolvedRehypePlugins}
        components={mergedComponents}
        {...restProps}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
});

export const storeSettings = (settings) => {
  setLocalStorageWithExpiry(
    C.EXPERIMENTAL_FEATURES_KEY,
    settings[C.EXPERIMENTAL_FEATURES_KEY],
    C.EXPIRY_TIME_IN_MINUTES,
  );
  setLocalStorageWithExpiry(
    C.DO_EXPLICIT_WEB_SEARCH_KEY,
    settings.query_research[C.DO_EXPLICIT_WEB_SEARCH_KEY],
    C.EXPIRY_TIME_IN_MINUTES,
  );
  setLocalStorageWithExpiry(
    C.ENABLE_SEARCH_LOCALIZATION_KEY,
    settings.query_research[C.ENABLE_SEARCH_LOCALIZATION_KEY],
    C.EXPIRY_TIME_IN_MINUTES,
  );
  setLocalStorageWithExpiry(
    C.SHARE_OF_CITATIONS_THRESHOLD_KEY,
    settings.optimize_content[C.SHARE_OF_CITATIONS_THRESHOLD_KEY],
    C.EXPIRY_TIME_IN_MINUTES,
  );
  setLocalStorageWithExpiry(
    C.URL_CITATION_SHARE_THRESHOLD_KEY,
    settings.optimize_content[C.URL_CITATION_SHARE_THRESHOLD_KEY],
    C.EXPIRY_TIME_IN_MINUTES,
  );
  setLocalStorageWithExpiry(
    C.DISTANCE_THRESHOLD_KEY,
    settings.optimize_content[C.DISTANCE_THRESHOLD_KEY],
    C.EXPIRY_TIME_IN_MINUTES,
  );
};

export const clearAuthStorage = () => {
  const preserved = {};
  if (typeof localStorage !== "undefined") {
    Object.keys(localStorage).forEach((key) => {
      if (
        key === "i18nextLng" ||
        key === C.I18N_LOCALE_KEY ||
        key.startsWith("elelem_tours_") ||
        key.startsWith("elelem_assistant_") ||
        key.startsWith("elelem_version_banner_") ||
        (C.SESSION_PRESERVE_PREFIX && key.startsWith(C.SESSION_PRESERVE_PREFIX))
      ) {
        const val = localStorage.getItem(key);
        if (val !== null) preserved[key] = val;
      }
    });
    localStorage.clear();
    Object.entries(preserved).forEach(([k, v]) => {
      localStorage.setItem(k, v);
    });
  }
};

export const loadSettings = () => {
  return {
    [C.EXPERIMENTAL_FEATURES_KEY]: getLocalStorageWithExpiry(
      C.EXPERIMENTAL_FEATURES_KEY,
    ),
    query_research: {
      [C.DO_EXPLICIT_WEB_SEARCH_KEY]: getLocalStorageWithExpiry(
        C.DO_EXPLICIT_WEB_SEARCH_KEY,
      ),
      [C.ENABLE_SEARCH_LOCALIZATION_KEY]: getLocalStorageWithExpiry(
        C.ENABLE_SEARCH_LOCALIZATION_KEY,
      ),
    },
    optimize_content: {
      [C.SHARE_OF_CITATIONS_THRESHOLD_KEY]: getLocalStorageWithExpiry(
        C.SHARE_OF_CITATIONS_THRESHOLD_KEY,
      ),
      [C.URL_CITATION_SHARE_THRESHOLD_KEY]: getLocalStorageWithExpiry(
        C.URL_CITATION_SHARE_THRESHOLD_KEY,
      ),
      [C.DISTANCE_THRESHOLD_KEY]: getLocalStorageWithExpiry(
        C.DISTANCE_THRESHOLD_KEY,
      ),
    },
  };
};

export { formatNotificationTime } from "./services/notificationService";
